/**
 * @module features/clients/components/ClientLifecycleModal
 * @description Accessible confirmation dialog for Status Toggling (Active <-> Suspended)
 * and Soft-Deletion (with atomic cascade user deactivation warning).
 */
import * as React from "react";
import { AlertTriangle, ShieldAlert, CheckCircle2, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClientSummary, ClientDetail } from "../types";
import { useUpdateClientStatus, useDeleteClient } from "../api/clientQueries";

export interface ClientLifecycleModalProps {
  client: ClientSummary | ClientDetail | null;
  mode: "status" | "delete" | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ClientLifecycleModal({
  client,
  mode,
  open,
  onClose,
  onSuccess,
}: ClientLifecycleModalProps) {
  const [confirmName, setConfirmName] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const statusMutation = useUpdateClientStatus();
  const deleteMutation = useDeleteClient();

  React.useEffect(() => {
    if (open) {
      setConfirmName("");
      setErrorMessage(null);
    }
  }, [open]);

  if (!client || !mode) return null;

  const isStatusMode = mode === "status";
  const newStatus = client.status === "active" ? "suspended" : "active";

  const handleStatusToggle = async () => {
    setErrorMessage(null);
    try {
      await statusMutation.mutateAsync({
        clientId: client.id,
        status: newStatus,
      });
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data?.error_message ||
          err?.message ||
          "Failed to update client status."
      );
    }
  };

  const handleDelete = async () => {
    if (confirmName.trim().toLowerCase() !== client.org_name.trim().toLowerCase()) {
      setErrorMessage("Please type the exact organization name to confirm deletion.");
      return;
    }

    setErrorMessage(null);
    try {
      await deleteMutation.mutateAsync(client.id);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data?.error_message ||
          err?.message ||
          "Failed to delete client organization."
      );
    }
  };

  const isPending = statusMutation.isPending || deleteMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent sheetOnMobile className="sm:max-w-md p-5 pb-[max(env(safe-area-inset-bottom),1.25rem)] sm:pb-5">
        <DialogHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <div
              className={`p-2.5 rounded-full ${
                isStatusMode
                  ? newStatus === "suspended"
                    ? "bg-amber-100 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400"
                    : "bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400"
                  : "bg-red-100 text-red-600 dark:bg-red-950/50 dark:text-red-400"
              }`}
            >
              {isStatusMode ? (
                newStatus === "suspended" ? (
                  <ShieldAlert className="h-5 w-5" />
                ) : (
                  <CheckCircle2 className="h-5 w-5" />
                )
              ) : (
                <Trash2 className="h-5 w-5" />
              )}
            </div>
            <div>
              <DialogTitle className="text-base font-semibold">
                {isStatusMode
                  ? `${newStatus === "suspended" ? "Suspend" : "Reactivate"} Organization`
                  : "Delete Organization"}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                <span className="font-medium text-foreground">{client.org_name}</span>
                <span className="text-[11px] font-mono bg-muted/60 px-1.5 py-0.5 rounded border border-border/70 text-foreground">
                  ID: {client.id}
                </span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {errorMessage && (
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-xs text-red-600 dark:text-red-400 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="text-xs text-muted-foreground space-y-3 py-1">
          {isStatusMode ? (
            newStatus === "suspended" ? (
              <p>
                Suspending this client will restrict portal access for its users. Their data and settings will be preserved, and the organization can be reactivated at any time.
              </p>
            ) : (
              <p>
                Reactivating this client will restore immediate portal access for its active linked users.
              </p>
            )
          ) : (
            <div className="space-y-3">
              <p className="text-red-600 dark:text-red-400 font-medium">
                Warning: This action will soft-delete the client organization and:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Atomically deactivate all {client.active_user_count} linked users.</li>
                <li>Immediately revoke all active sessions and refresh tokens.</li>
                <li>Free up organizational identifiers.</li>
              </ul>
              <div className="pt-2">
                <label className="text-foreground font-semibold block mb-1">
                  Type <span className="font-mono text-zinc-900 dark:text-white underline">{client.org_name}</span> to confirm:
                </label>
                <Input
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder="Enter organization name"
                  className="h-9 text-xs"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          {isStatusMode ? (
            <Button
              size="sm"
              variant={newStatus === "suspended" ? "destructive" : "default"}
              onClick={handleStatusToggle}
              disabled={isPending}
            >
              {isPending
                ? "Updating..."
                : `${newStatus === "suspended" ? "Suspend" : "Reactivate"} Client`}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDelete}
              disabled={
                isPending ||
                confirmName.trim().toLowerCase() !== client.org_name.trim().toLowerCase()
              }
            >
              {isPending ? "Deleting..." : "Permanently Delete"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
